import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";
import {
    Plus, Search, Filter, MoreVertical, Eye, Edit2, Trash2, Users, User,
    IndianRupee, TrendingDown, CheckCircle2, FileText, Printer, Download,
    DollarSign, Loader2, RefreshCcw, Settings, Briefcase, Play, ArrowRight, X, Layers,
    Landmark, ShieldCheck, Calendar, Info, Layout
} from 'lucide-react';
import { toast } from 'sonner';
import API from '../api/api01';

const cn = (...classes: any[]) => classes.filter(Boolean).join(' ');

export const SalaryPayout = ({ initialTab = 'runs' }: { initialTab?: string }) => {
    const [activeTab, setActiveTab] = useState(initialTab);
    const [loading, setLoading] = useState(true);
    const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
    const [isFinalizing, setIsFinalizing] = useState(false);

    // Sync activeTab when initialTab prop changes
    useEffect(() => {
        setActiveTab(initialTab);
    }, [initialTab]);

    // Data State
    const [components, setComponents] = useState<any[]>([]);
    const [structures, setStructures] = useState<any[]>([]);
    const [assignments, setAssignments] = useState<any[]>([]);
    const [payrollRuns, setPayrollRuns] = useState<any[]>([]);
    const [profiles, setProfiles] = useState<any[]>([]);

    // Fetching Logic
    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const [compRes, structRes, assignRes, runRes, userRes] = await Promise.all([
                API.get('/salary/components'),
                API.get('/salary/structures'),
                API.get('/salary/assignments'),
                API.get('/salary/payroll-run'),
                API.get('/superadmin/users')
            ]);
            setComponents(compRes.data || []);
            setStructures(structRes.data || []);
            setAssignments(assignRes.data || []);
            setPayrollRuns(runRes.data || []);
            setProfiles(userRes.data || []);

            if ((compRes.data?.length || 0) === 0 &&
                (structRes.data?.length || 0) === 0 &&
                (assignRes.data?.length || 0) === 0 &&
                (runRes.data?.length || 0) === 0) {
                toast.error("No records found");
            } else {
                toast.success("Payroll data synchronized");
            }
        } catch (err) {
            console.error("Fetch error:", err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);


    return (
        <TooltipProvider>
            <div className="min-h-screen bg-background p-6 lg:p-8">
                <div className="max-w-[1600px] mx-auto space-y-8">
                    {/* Professional Sub-navigation */}
                    <div className="sticky top-4 z-50 bg-card/80 backdrop-blur-xl border border-white/10 p-2 rounded-[2rem] shadow-2xl flex flex-col md:flex-row items-center justify-between gap-4 animate-fade-in">
                        <div className="flex items-center gap-6 px-4">
                            <div className="flex flex-col">
                                <h1 className="text-xl font-bold tracking-tight text-foreground flex items-center gap-2 leading-none">
                                    <IndianRupee className="h-5 w-5 text-blue-600" /> <span className="text-slate-900 dark:text-white">Payroll</span> <span className="text-blue-600/60">Hub</span>
                                </h1>
                                <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground/60 mt-1">Superadmin Control</p>
                            </div>

                            <div className="h-8 w-px bg-border/50 hidden md:block" />
                            <nav className="flex items-center gap-1">
                                <div className="flex items-center text-[11px] font-bold uppercase tracking-widest">
                                    <button
                                        onClick={() => { setActiveTab('runs'); setSelectedRunId(null); setIsFinalizing(false); }}
                                        className={cn(
                                            "px-4 py-2 rounded-xl transition-all",
                                            !selectedRunId ? "text-blue-600 bg-blue-50 dark:bg-blue-900/20" : "text-muted-foreground hover:text-foreground"
                                        )}
                                    >
                                        Monthly Cycle
                                    </button>

                                    {selectedRunId && (
                                        <>
                                            <ArrowRight className="h-3 w-3 mx-2 text-muted-foreground/30" />
                                            <button
                                                onClick={() => setIsFinalizing(false)}
                                                className={cn(
                                                    "px-4 py-2 rounded-xl transition-all",
                                                    !isFinalizing ? "text-blue-600 bg-blue-50 dark:bg-blue-900/20" : "text-muted-foreground hover:text-foreground"
                                                )}
                                            >
                                                Review
                                            </button>
                                        </>
                                    )}

                                    {isFinalizing && (
                                        <>
                                            <ArrowRight className="h-3 w-3 mx-2 text-muted-foreground/30" />
                                            <span className="px-4 py-2 rounded-xl text-blue-600 bg-blue-50 dark:bg-blue-900/20 flex items-center gap-2">
                                                Finalize
                                                <span className="h-1.5 w-1.5 rounded-full bg-blue-600 animate-pulse" />
                                            </span>
                                        </>
                                    )}
                                </div>
                            </nav>

                        </div>
                        <div className="flex items-center gap-3 pr-2">
                            <div className="flex flex-col text-right hidden lg:block mr-2">
                                <span className="text-[10px] font-bold uppercase text-muted-foreground">System Status</span>
                                <span className="text-xs font-bold text-emerald-500 flex items-center justify-end gap-1.5">
                                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" /> Live Now
                                </span>
                            </div>
                            <Button variant="outline" size="icon" onClick={fetchData} className="rounded-full h-11 w-11 hover:rotate-180 transition-transform duration-500 border-white/10 bg-background/50 backdrop-blur-sm">
                                <RefreshCcw className={cn("h-4 w-4", loading && "animate-spin")} />
                            </Button>
                        </div>
                    </div>

                    <div className="space-y-10 pt-4 animate-fade-in">
                        {/* The internal TabsList is removed as sidebar handles navigation now */}

                        {/* Payroll Runs View */}
                        {activeTab === 'runs' && (
                            <div className="space-y-6">
                                {selectedRunId ? (
                                    <PayrollDetailView
                                        runId={selectedRunId}
                                        onBack={() => {
                                            setSelectedRunId(null);
                                            setIsFinalizing(false);
                                            fetchData();
                                        }}
                                        setIsFinalizing={setIsFinalizing}
                                    />
                                ) : (
                                    <PayrollRunsTab
                                        runs={payrollRuns}
                                        profiles={profiles}
                                        loading={loading}
                                        onRefresh={fetchData}
                                        onSelectRun={setSelectedRunId}
                                    />
                                )}
                            </div>
                        )}

                        {/* Assignments View */}
                        {activeTab === 'assign' && (
                            <div className="space-y-6">
                                <AssignmentsTab
                                    assignments={assignments}
                                    structures={structures}
                                    profiles={profiles}
                                    loading={loading}
                                    onRefresh={fetchData}
                                />
                            </div>
                        )}

                        {/* Configuration View */}
                        {activeTab === 'config' && (
                            <div className="space-y-6">
                                <ConfigurationTab
                                    components={components}
                                    structures={structures}
                                    loading={loading}
                                    onRefresh={fetchData}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </TooltipProvider>
    );
};

// --- Sub-components for Tabs ---

const PayrollRunsTab = ({ runs, profiles, loading, onRefresh, onSelectRun }: any) => {
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear()
    });

    const handleInitiate = async () => {
        try {
            setSubmitting(true);
            await API.post('/salary/payroll-run', formData);
            toast.success("Payroll run initiated successfully");
            setIsCreateModalOpen(false);
            onRefresh();
        } catch (err: any) {
            toast.error(err.response?.data?.detail || "Failed to initiate payroll run");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="space-y-10">
            {/* Dashboard Summary Widgets */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                    { label: 'Projected Outflow', value: `₹${runs.reduce((acc: any, curr: any) => acc + (curr.total_net || 0), 0).toLocaleString()}`, icon: IndianRupee, color: 'text-primary', sub: 'Active Month', trend: '+4.2%' },
                    { label: 'Active Personnel', value: profiles.length > 0 ? `${profiles.length} Staff` : 'Scanning...', icon: Users, color: 'text-indigo-500', sub: 'On Record', trend: 'Live' },
                    { label: 'Efficiency Score', value: '98.5%', icon: TrendingDown, color: 'text-emerald-500', sub: 'Processing Speed', trend: 'Optimized' },
                ].map((stat, i) => (
                    <div key={i} className="bg-card border border-white/10 p-6 rounded-[2rem] flex items-center gap-5 group hover:translate-y-[-4px] transition-all duration-300 shadow-lg">
                        <div className={cn("h-14 w-14 rounded-2xl flex items-center justify-center bg-background/50 shadow-inner", stat.color)}>
                            <stat.icon className="h-7 w-7" />
                        </div>
                        <div className="flex-1">
                            <p className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/80 mb-1">{stat.label}</p>
                            <div className="flex items-baseline justify-between">
                                <h3 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">{stat.value}</h3>
                                <span className="text-[10px] font-bold bg-muted px-2 py-0.5 rounded-full uppercase tracking-widest opacity-70">{stat.trend}</span>
                            </div>
                        </div>
                    </div>

                ))}
            </div>

            <div className="flex justify-between items-center">
                <div className="space-y-1">
                    <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white flex items-center gap-3">
                        <Play className="h-6 w-6 text-blue-600 fill-blue-600/20" /> Monthly Cycles
                    </h2>
                    <p className="text-[13px] font-medium text-slate-500 dark:text-slate-400">History of generated and finalized payroll runs</p>
                </div>
                <Button onClick={() => setIsCreateModalOpen(true)} className="rounded-2xl shadow-lg shadow-blue-500/20 h-14 px-8 bg-blue-600 hover:bg-blue-700 text-white transition-all active:scale-95">
                    <Plus className="h-5 w-5 mr-3" />
                    <span className="text-[13px] font-bold uppercase tracking-widest">New Cycle</span>
                </Button>
            </div>


            <Card className="bg-card border border-white/10 shadow-sm rounded-2xl overflow-hidden">
                <Table>
                    <TableHeader className="bg-muted/30">
                        <TableRow className="border-white/10 hover:bg-transparent">
                            <TableHead className="pl-6 text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70 py-4">Period</TableHead>
                            <TableHead className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70 py-4">Status</TableHead>
                            <TableHead className="text-right text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70 py-4">Total Gross</TableHead>
                            <TableHead className="text-right text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70 py-4">Total Net</TableHead>
                            <TableHead className="text-right pr-6 text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70 py-4">Action</TableHead>
                        </TableRow>
                    </TableHeader>

                    <TableBody>
                        {loading && !runs.length ? (
                            <TableRow>
                                <TableCell colSpan={5} className="text-center py-12"><Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" /></TableCell>
                            </TableRow>
                        ) : runs.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} className="text-center py-20 text-muted-foreground group">
                                    <div className="flex flex-col items-center gap-4 animate-in fade-in zoom-in-95 duration-500">
                                        <div className="h-20 w-20 rounded-[2rem] bg-amber-50 dark:bg-amber-900/10 flex items-center justify-center text-amber-500 group-hover:scale-110 transition-transform shadow-inner">
                                            <Play className="h-10 w-10 opacity-40" />
                                        </div>
                                        <div className="space-y-2 max-w-[280px]">
                                            <p className="text-xl font-bold text-foreground">No Monthly Cycles</p>
                                            <p className="text-sm font-medium leading-relaxed">Initiate your first payroll engine run to begin tracking monthly disbursements.</p>
                                        </div>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ) : (
                            runs.map((run: any) => (
                                <TableRow
                                    key={run.id}
                                    className="hover:bg-muted/30 transition-colors cursor-pointer group"
                                    onClick={() => onSelectRun(run.id)}
                                >
                                    <TableCell className="font-medium pl-6 py-4">
                                        {new Date(run.year, run.month - 1).toLocaleString('default', { month: 'long', year: 'numeric' })}
                                    </TableCell>
                                    <TableCell>
                                        <Badge
                                            variant={run.status === 'Finalized' ? 'default' : 'secondary'}
                                            className={cn(
                                                "text-[10px] font-bold uppercase tracking-widest border-none px-2 py-0.5",
                                                run.status === 'Draft' && "bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400",
                                                run.status === 'Processing' && "bg-blue-100 text-blue-700 dark:bg-blue-500/10 dark:text-blue-400",
                                                run.status === 'Finalized' && "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400"
                                            )}
                                        >
                                            {run.status}
                                        </Badge>

                                    </TableCell>
                                    <TableCell className="text-right font-medium">₹{run.total_gross.toLocaleString()}</TableCell>
                                    <TableCell className="text-right font-bold text-primary">₹{run.total_net.toLocaleString()}</TableCell>
                                    <TableCell className="text-right pr-6">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="text-[12px] font-bold uppercase tracking-widest hover:bg-blue-50 dark:hover:bg-blue-900/20 text-blue-600 transition-all"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onSelectRun(run.id);
                                            }}
                                        >
                                            Review <ArrowRight className="ml-2 h-4 w-4" />
                                        </Button>

                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </Card>

            <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
                <DialogContent className="sm:max-w-[500px] rounded-[2.5rem] p-0 overflow-hidden border border-white/10 shadow-[0_32px_80px_rgba(0,0,0,0.3)] bg-card/95 backdrop-blur-2xl">
                    <DialogHeader className="bg-gradient-to-br from-primary via-blue-600 to-indigo-700 px-10 py-12 text-white relative overflow-hidden">
                        <div className="absolute -right-10 -bottom-10 opacity-10 rotate-12 pointer-events-none">
                            <Play size={220} />
                        </div>
                        <div className="relative z-10 flex flex-col gap-2">
                            <div className="bg-white/10 backdrop-blur-md w-fit px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest border border-white/10">
                                Phase: Generation
                            </div>
                            <DialogTitle className="text-2xl font-bold tracking-tight text-white">Initiate Cycle</DialogTitle>
                            <DialogDescription className="text-white/70 font-medium text-sm">
                                Launch the monthly payroll engine and generate disbursement reports.
                            </DialogDescription>
                        </div>

                    </DialogHeader>
                    <div className="p-10 space-y-8 bg-background">
                        {/* Helpful Item: Pre-analysis Card */}
                        <div className="bg-background/50 border border-white/5 p-6 rounded-3xl flex items-center justify-between shadow-inner">
                            <div className="flex items-center gap-4">
                                <div className="h-12 w-12 bg-primary/10 rounded-2xl flex items-center justify-center text-primary">
                                    <Users className="h-6 w-6" />
                                </div>
                                <div>
                                    <p className="text-[10px] font-bold uppercase text-muted-foreground tracking-widest leading-none mb-1">Eligible Staff</p>
                                    <p className="text-xl font-bold text-foreground">Pending Scan...</p>
                                </div>
                            </div>
                            <div className="text-right">
                                <p className="text-[10px] font-bold uppercase text-primary/60 tracking-widest leading-none mb-1">Status</p>
                                <Badge variant="outline" className="text-[10px] border-primary/20 bg-primary/5 text-primary">Ready to sync</Badge>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-6">
                            <div className="space-y-3">
                                <label className="text-[11px] font-bold uppercase tracking-[0.1em] text-muted-foreground/80 flex items-center gap-2">
                                    <div className="h-1.5 w-1.5 rounded-full bg-primary" /> Month
                                </label>
                                <Select
                                    value={formData.month.toString()}
                                    onValueChange={(v) => setFormData({ ...formData, month: parseInt(v) })}
                                >
                                    <SelectTrigger className="rounded-2xl h-16 border-muted-foreground/20 bg-muted/40 focus:ring-primary/20 transition-all text-lg font-bold shadow-inner">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="rounded-2xl border-none shadow-2xl p-2 backdrop-blur-xl">
                                        {Array.from({ length: 12 }, (_, i) => (
                                            <SelectItem key={i + 1} value={(i + 1).toString()} className="h-12 rounded-xl mb-1 text-base">
                                                {new Date(2000, i).toLocaleString('default', { month: 'long' })}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-3">
                                <label className="text-[11px] font-bold uppercase tracking-[0.1em] text-muted-foreground/80 flex items-center gap-2">
                                    <div className="h-1.5 w-1.5 rounded-full bg-indigo-500" /> Year
                                </label>
                                <Select
                                    value={formData.year.toString()}
                                    onValueChange={(v) => setFormData({ ...formData, year: parseInt(v) })}
                                >
                                    <SelectTrigger className="rounded-2xl h-16 border-muted-foreground/20 bg-muted/40 focus:ring-primary/20 transition-all text-lg font-bold shadow-inner">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="rounded-2xl border-none shadow-2xl p-2 backdrop-blur-xl">
                                        {[2025, 2026, 2027].map(y => (
                                            <SelectItem key={y} value={y.toString()} className="h-12 rounded-xl mb-1 text-base">{y}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </div>
                    <div className="px-10 pb-10 flex gap-4">
                        <Button
                            variant="ghost"
                            onClick={() => setIsCreateModalOpen(false)}
                            className="rounded-2xl h-16 font-bold text-muted-foreground hover:bg-muted transition-all active:scale-95"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleInitiate}
                            disabled={submitting}
                            className="rounded-2xl flex-1 h-16 text-lg font-bold bg-primary hover:bg-primary/90 shadow-2xl shadow-primary/40 group active:scale-[0.98] transition-all"
                        >
                            {submitting ? (
                                <Loader2 className="h-6 w-6 animate-spin" />
                            ) : (
                                <>
                                    Launch Engine
                                    <ArrowRight className="ml-3 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                                </>
                            )}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

const AssignmentsTab = ({ assignments, structures, profiles, loading, onRefresh }: any) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [selectedRole, setSelectedRole] = useState<string>('');
    const [personnelSearch, setPersonnelSearch] = useState<string>('');
    const [formData, setFormData] = useState({
        profile_id: '',
        structure_id: '',
        ctc: '',
        effective_date: new Date().toISOString().split('T')[0],
        id: '' // Add id field for editing
    });

    const filteredProfiles = useMemo(() => {
        if (!selectedRole) return [];
        const roleQuery = selectedRole.toLowerCase();
        const searchVal = personnelSearch.toLowerCase();

        return profiles.filter((p: any) => {
            const userRole = (p.role || '').toLowerCase();
            const fullName = (p.full_name || '').toLowerCase();
            const profileId = (p.profile_id || '').toLowerCase();

            // Match Role
            let roleMatched = false;
            if (roleQuery === 'admin') roleMatched = userRole.includes('admin');
            else if (roleQuery === 'teacher') roleMatched = userRole.includes('teach') || userRole.includes('mentor');
            else if (roleQuery === 'student') roleMatched = userRole.includes('stud');
            else if (roleQuery === 'client') roleMatched = userRole.includes('clien');
            else if (roleQuery === 'customer') roleMatched = userRole.includes('custom');
            else roleMatched = userRole === roleQuery;

            if (!roleMatched) return false;

            // Match Search
            if (!searchVal) return true;
            return fullName.includes(searchVal) || profileId.includes(searchVal);
        });
    }, [profiles, selectedRole, personnelSearch]);

    const selectedPerson = useMemo(() => {
        return profiles.find((p: any) => p.id === formData.profile_id);
    }, [profiles, formData.profile_id]);

    const handleAssign = async () => {
        if (!formData.profile_id || !formData.structure_id || !formData.ctc) {
            toast.error("Please fill all required fields");
            return;
        }
        try {
            setSubmitting(true);
            if (formData.id) {
                await API.put(`/salary/assignments/${formData.id}`, formData);
                toast.success("Salary assignment updated successfully");
            } else {
                await API.post('/salary/assign', formData);
                toast.success("Salary structure assigned successfully");
            }
            setIsModalOpen(false);
            onRefresh();
        } catch (err: any) {
            toast.error(err.response?.data?.detail || "Failed to save assignment");
        } finally {
            setSubmitting(false);
        }
    };

    const handleEdit = (assignment: any) => {
        setFormData({
            profile_id: assignment.profile_id,
            structure_id: assignment.structure_id,
            ctc: assignment.ctc.toString(),
            effective_date: assignment.effective_date.split('T')[0],
            id: assignment.id
        });
        setSelectedRole(assignment.role || '');
        setIsModalOpen(true);
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm("Are you sure you want to delete this assignment?")) return;
        try {
            await API.delete(`/salary/assignments/${id}`);
            toast.success("Assignment deleted successfully");
            onRefresh();
        } catch (err: any) {
            toast.error(err.response?.data?.detail || "Failed to delete assignment");
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white flex items-center gap-2">
                    <Briefcase className="h-5 w-5 text-blue-600" /> Staff Salary Assignments
                </h2>
                <Button className="rounded-full shadow-lg shadow-blue-500/20 h-11 px-6 bg-blue-600 hover:bg-blue-700 text-white" onClick={() => setIsModalOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" /> <span className="text-[13px] font-bold uppercase tracking-widest">Assign Structure</span>
                </Button>
            </div>


            <Card className="bg-card border border-white/10 shadow-sm rounded-2xl overflow-hidden">
                <Table>
                    <TableHeader className="bg-muted/30">
                        <TableRow className="border-white/10 hover:bg-transparent">
                            <TableHead className="pl-6 text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70 py-4">Staff Name</TableHead>
                            <TableHead className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70 py-4">Structure</TableHead>
                            <TableHead className="text-right text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70 py-4">CTC (Annual)</TableHead>
                            <TableHead className="text-right text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70 py-4">Effective From</TableHead>
                            <TableHead className="w-[80px] pr-6"></TableHead>
                        </TableRow>
                    </TableHeader>

                    <TableBody>
                        {loading && !assignments.length ? (
                            <TableRow>
                                <TableCell colSpan={5} className="text-center py-12"><Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" /></TableCell>
                            </TableRow>
                        ) : assignments.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} className="text-center py-20 text-muted-foreground group">
                                    <div className="flex flex-col items-center gap-4 animate-in fade-in zoom-in-95 duration-500">
                                        <div className="h-20 w-20 rounded-[2rem] bg-indigo-50 dark:bg-indigo-900/10 flex items-center justify-center text-indigo-500 group-hover:scale-110 transition-transform shadow-inner">
                                            <Users className="h-10 w-10 opacity-40" />
                                        </div>
                                        <div className="space-y-2 max-w-[280px]">
                                            <p className="text-xl font-bold text-foreground">No Sub-menu Assignments</p>
                                            <p className="text-sm font-medium leading-relaxed">Connect your personnel to defined salary structures to manage their compensation records.</p>
                                        </div>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ) : (
                            assignments.map((a: any) => (
                                <TableRow key={a.id} className="hover:bg-muted/30 group">
                                    <TableCell className="font-medium pl-6 py-4">
                                        <div className="flex items-center gap-3">
                                            <div className="h-9 w-9 rounded-full bg-blue-50 dark:bg-blue-900/40 flex items-center justify-center text-blue-600 dark:text-blue-400 font-bold text-xs uppercase">
                                                {a.full_name?.split(' ').map((n: string) => n[0]).join('').substring(0, 2)}
                                            </div>
                                            <div>
                                                <div className="text-[13px] font-bold text-slate-900 dark:text-white leading-tight">{a.full_name}</div>
                                                <div className="text-[10px] text-muted-foreground/60 font-bold uppercase tracking-widest mt-0.5">{a.profile_id?.substring(0, 8)}</div>
                                            </div>
                                        </div>
                                    </TableCell>
                                    <TableCell><Badge variant="outline" className="rounded-full text-[10px] font-bold uppercase tracking-widest border-slate-200 dark:border-white/10 bg-transparent px-2.5 py-0.5">{a.structure_name}</Badge></TableCell>
                                    <TableCell className="text-right text-[13px] font-bold text-slate-900 dark:text-white">₹{a.ctc?.toLocaleString()}</TableCell>
                                    <TableCell className="text-right text-[13px] font-medium text-slate-500 dark:text-slate-400">{new Date(a.effective_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</TableCell>
                                    <TableCell className="pr-6">

                                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <Button variant="ghost" size="icon" onClick={() => handleEdit(a)}><Edit2 className="h-4 w-4" /></Button>
                                                </TooltipTrigger>
                                                <TooltipContent side="top">Edit</TooltipContent>
                                            </Tooltip>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <Button variant="ghost" size="icon" onClick={() => handleDelete(a.id)} className="text-destructive"><Trash2 className="h-4 w-4" /></Button>
                                                </TooltipTrigger>
                                                <TooltipContent side="top">Delete</TooltipContent>
                                            </Tooltip>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </Card>

            <Dialog open={isModalOpen} onOpenChange={(open) => {
                setIsModalOpen(open);
                if (!open) {
                    setSelectedRole('');
                    setPersonnelSearch('');
                    setFormData({
                        profile_id: '',
                        structure_id: '',
                        ctc: '',
                        effective_date: new Date().toISOString().split('T')[0],
                        id: ''
                    });
                }
            }}>
                <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto rounded-[2.5rem] p-0 border border-white/10 shadow-[0_40px_100px_rgba(0,0,0,0.4)] bg-card/95 backdrop-blur-2xl scrollbar-hide">
                    <DialogHeader className="bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-primary px-10 py-12 text-white relative overflow-hidden">
                        <div className="absolute -right-8 -top-8 opacity-10 rotate-12 pointer-events-none">
                            <Briefcase size={260} />
                        </div>
                        <div className="relative z-10 flex items-center justify-between">
                            <div className="space-y-2">
                                <Badge variant="outline" className="text-[10px] font-bold uppercase tracking-widest text-blue-200 border-white/20 bg-white/5">Recruitment Tool</Badge>
                                <DialogTitle className="text-2xl font-bold tracking-tight text-white dark:text-white">
                                    {formData.id ? "Edit Assignment" : "Assign Structure"}
                                </DialogTitle>
                                <DialogDescription className="text-white/60 font-medium text-sm max-w-[400px]">
                                    {formData.id ? "Update the salary architecture." : "Provision an automated salary architecture for the selected colleague."}
                                </DialogDescription>
                            </div>

                            <div className="hidden md:flex flex-col items-end">
                                <div className="p-4 bg-white/10 rounded-2xl border border-white/20 backdrop-blur-md">
                                    <p className="text-[10px] font-bold uppercase text-white/60 tracking-widest text-right mb-1">Monthly Approx.</p>
                                    <p className="text-2xl font-bold text-emerald-400">₹{(parseInt(formData.ctc) / 12 || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
                                </div>
                            </div>
                        </div>
                    </DialogHeader>
                    <div className="p-10 bg-background">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                            <div className="space-y-8">
                                <div className="space-y-3">
                                    <label className="text-[11px] font-bold uppercase tracking-[0.1em] text-muted-foreground/80 flex items-center gap-2">
                                        <Layers className="h-3 w-3 text-primary" /> 1. Select Role
                                    </label>
                                    <div className="grid grid-cols-3 gap-2">
                                        {['Admin', /* 'Teacher', 'Student', */ 'Client', 'Customer'].map((role) => (
                                            <button
                                                key={role}
                                                disabled={!!formData.id}
                                                onClick={() => {
                                                    setSelectedRole(role);
                                                    setPersonnelSearch('');
                                                    setFormData({ ...formData, profile_id: '' });
                                                }}
                                                className={cn(
                                                    "px-3 py-2 rounded-xl text-[10px] font-bold uppercase tracking-wider transition-all border",
                                                    selectedRole === role
                                                        ? "bg-primary text-white border-primary shadow-lg shadow-primary/20"
                                                        : "bg-muted/40 text-muted-foreground border-border/50 hover:bg-muted",
                                                    formData.id && "opacity-50 cursor-not-allowed"
                                                )}
                                            >
                                                {role}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-500">
                                    <div className="flex items-center justify-between">
                                        <label className="text-[11px] font-bold uppercase tracking-[0.1em] text-muted-foreground/80 flex items-center gap-2">
                                            <User className="h-3 w-3 text-primary" /> {formData.profile_id && selectedPerson ? (
                                                <span className="animate-in fade-in slide-in-from-left-1">2. Selected: <span className="text-primary">{selectedPerson.full_name}</span> <span className="text-[10px] text-muted-foreground/60 ml-2">({selectedPerson.client_name})</span></span>
                                            ) : (
                                                `2. Identify ${selectedRole || 'Personnel'}`
                                            )}
                                        </label>
                                        {selectedRole && (
                                            <div className="relative">
                                                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-2.5 w-2.5 text-muted-foreground" />
                                                <input
                                                    type="text"
                                                    placeholder="Quick search..."
                                                    value={personnelSearch}
                                                    onChange={(e) => setPersonnelSearch(e.target.value)}
                                                    className="pl-8 pr-3 py-1 bg-muted/60 dark:bg-slate-900/80 border-none rounded-full text-[10px] focus:ring-1 focus:ring-primary/30 outline-none w-32 tracking-tight font-medium"
                                                />
                                            </div>
                                        )}
                                    </div>

                                    <div className={cn(
                                        "rounded-2xl border border-muted-foreground/20 bg-muted/20 dark:bg-slate-900/40 p-2 min-h-[180px] max-h-[220px] overflow-y-auto scrollbar-hide",
                                        !selectedRole && "opacity-40"
                                    )}>
                                        {!selectedRole ? (
                                            <div className="h-full flex flex-col items-center justify-center text-center py-12 gap-2">
                                                <Layers className="h-6 w-6 text-muted-foreground/30" />
                                                <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Select a role first</span>
                                            </div>
                                        ) : filteredProfiles.length === 0 ? (
                                            <div className="h-full flex flex-col items-center justify-center text-center py-10 gap-2 italic">
                                                <Search className="h-6 w-6 text-muted-foreground/20" />
                                                <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">No matching {selectedRole}s</span>
                                            </div>
                                        ) : (
                                            <div className="space-y-1.5">
                                                {filteredProfiles.map((p: any) => (
                                                    <button
                                                        key={p.id}
                                                        disabled={!!formData.id}
                                                        onClick={() => setFormData({ ...formData, profile_id: p.id })}
                                                        className={cn(
                                                            "w-full flex items-center gap-3 p-2.5 rounded-xl transition-all border text-left group",
                                                            formData.profile_id === p.id
                                                                ? "bg-primary/10 border-primary shadow-sm"
                                                                : "bg-background/50 border-transparent hover:border-border hover:bg-background",
                                                            formData.id && formData.profile_id !== p.id && "opacity-50"
                                                        )}
                                                    >
                                                        <div className={cn(
                                                            "h-10 w-10 rounded-full flex items-center justify-center font-bold text-xs transition-colors",
                                                            formData.profile_id === p.id
                                                                ? "bg-primary text-white"
                                                                : "bg-muted text-muted-foreground group-hover:bg-muted-foreground/20"
                                                        )}>
                                                            {p.full_name?.split(' ').map((n: string) => n[0]).join('').substring(0, 2)}
                                                        </div>
                                                        <div className="min-w-0 flex-1">
                                                            <div className="font-bold text-xs text-foreground truncate">{p.full_name}</div>
                                                            <div className="text-[9px] text-muted-foreground uppercase font-bold tracking-[0.05em] flex items-center gap-1.5 truncate">
                                                                <span className="text-primary/70">{p.client_name}</span>
                                                                <span className="opacity-30">•</span>
                                                                <span className="truncate">{p.role || 'Unassigned'}</span>
                                                            </div>
                                                        </div>
                                                        {formData.profile_id === p.id && (
                                                            <div className="h-5 w-5 rounded-full bg-primary flex items-center justify-center text-white shrink-0 animate-in zoom-in-50 duration-200">
                                                                <CheckCircle2 className="h-3 w-3" />
                                                            </div>
                                                        )}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="space-y-3">
                                    <label className="text-[11px] font-bold uppercase tracking-[0.1em] text-muted-foreground/80 flex items-center gap-2">
                                        <Settings className="h-3 w-3 text-indigo-500" /> Pay Template
                                    </label>
                                    <Select
                                        value={formData.structure_id}
                                        onValueChange={(v) => setFormData({ ...formData, structure_id: v })}
                                    >
                                        <SelectTrigger className="rounded-2xl h-16 border-muted-foreground/20 bg-muted/40 dark:bg-slate-900/50 transition-all text-base font-bold shadow-inner">
                                            <SelectValue placeholder="Pick blueprint" />
                                        </SelectTrigger>
                                        <SelectContent className="rounded-2xl border-border bg-popover shadow-2xl p-2 backdrop-blur-xl">
                                            {structures.map((s: any) => (
                                                <SelectItem key={s.id} value={s.id} className="h-12 rounded-xl mb-1 font-bold text-sm hover:bg-muted focus:bg-muted cursor-pointer transition-colors">
                                                    {s.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            <div className="space-y-8">
                                <div className="space-y-3">
                                    <label className="text-[11px] font-bold uppercase tracking-[0.1em] text-muted-foreground/80 flex items-center gap-2">
                                        <IndianRupee className="h-3 w-3 text-emerald-500" /> Annual Package (CTC)
                                    </label>
                                    <div className="relative group">
                                        <div className="absolute inset-y-0 left-6 flex items-center pointer-events-none">
                                            <span className="text-xl font-bold text-primary italic">₹</span>
                                        </div>
                                        <Input
                                            type="number"
                                            value={formData.ctc}
                                            onChange={(e) => setFormData({ ...formData, ctc: e.target.value })}
                                            placeholder="Enter CTC"
                                            className="pl-12 rounded-2xl h-16 border-white/5 bg-background focus:ring-primary/20 transition-all text-xl font-bold shadow-inner"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-3">
                                    <label className="text-[11px] font-bold uppercase tracking-[0.1em] text-muted-foreground/80 flex items-center gap-2">
                                        <FileText className="h-3 w-3 text-amber-500" /> Start Date
                                    </label>
                                    <Input
                                        type="date"
                                        value={formData.effective_date}
                                        onChange={(e) => setFormData({ ...formData, effective_date: e.target.value })}
                                        className="rounded-2xl h-16 border-white/5 bg-background focus:ring-primary/20 transition-all font-bold text-sm px-6 shadow-inner"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Live Estimation Badge */}
                        <div className="mt-10 p-4 rounded-2xl bg-primary/5 border border-primary/10 flex items-center justify-center gap-3 animate-pulse">
                            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                            <span className="text-[10px] font-bold uppercase tracking-widest text-primary/80">Automated calculation engine active for validation</span>
                        </div>
                    </div>
                    <div className="px-10 pb-10 flex gap-4">
                        <Button
                            variant="ghost"
                            onClick={() => setIsModalOpen(false)}
                            className="rounded-2xl h-16 font-bold text-muted-foreground hover:bg-muted transition-all"
                        >
                            Review Later
                        </Button>
                        <Button
                            onClick={handleAssign}
                            disabled={submitting}
                            className="rounded-2xl flex-1 h-16 text-lg font-bold bg-primary hover:bg-primary/90 shadow-2xl shadow-primary/40 group active:scale-[0.98] transition-all"
                        >
                            {submitting ? <Loader2 className="h-6 w-6 animate-spin" /> : (
                                <>
                                    Confirm Assignment
                                    <ArrowRight className="ml-3 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                                </>
                            )}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

const ConfigurationTab = ({ components, structures, loading, onRefresh }: any) => {
    const [isCompModalOpen, setIsCompModalOpen] = useState(false);
    const [isStructModalOpen, setIsStructModalOpen] = useState(false);
    const [isManageCompOpen, setIsManageCompOpen] = useState(false);
    const [selectedStructure, setSelectedStructure] = useState<any>(null);
    const [submitting, setSubmitting] = useState(false);

    const [compData, setCompData] = useState({
        name: '',
        type: 'Earning',
        calculation_type: 'Fixed',
        default_value: 0,
        is_taxable: true,
        id: '' // Add id field for editing
    });

    const [structData, setStructData] = useState({
        name: '',
        description: '',
        id: '' // Add id field for editing
    });

    const handleEditComponent = (comp: any) => {
        setCompData({
            name: comp.name,
            type: comp.type,
            calculation_type: comp.calculation_type,
            default_value: comp.default_value,
            is_taxable: comp.is_taxable,
            id: comp.id
        });
        setIsCompModalOpen(true);
    };

    const handleDeleteComponent = async (id: string) => {
        if (!window.confirm("Are you sure you want to delete this component?")) return;
        try {
            await API.delete(`/salary/components/${id}`);
            toast.success("Component deleted successfully");
            onRefresh();
        } catch (err: any) {
            toast.error(err.response?.data?.detail || "Failed to delete component");
        }
    };

    const handleEditStructure = (struct: any) => {
        setStructData({
            name: struct.name,
            description: struct.description,
            id: struct.id
        });
        setIsStructModalOpen(true);
    };

    const handleDeleteStructure = async (id: string) => {
        if (!window.confirm("Are you sure you want to delete this structure?")) return;
        try {
            await API.delete(`/salary/structures/${id}`);
            toast.success("Structure deleted successfully");
            onRefresh();
        } catch (err: any) {
            toast.error(err.response?.data?.detail || "Failed to delete structure");
        }
    };

    const handleSaveComponent = async () => {
        if (!compData.name) {
            toast.error("Component name is required");
            return;
        }
        try {
            setSubmitting(true);
            if (compData.id) {
                await API.put(`/salary/components/${compData.id}`, compData);
                toast.success("Component updated successfully");
            } else {
                await API.post('/salary/components', compData);
                toast.success("Component created successfully");
            }
            setIsCompModalOpen(false);
            setCompData({ name: '', type: 'Earning', calculation_type: 'Fixed', default_value: 0, is_taxable: true, id: '' });
            onRefresh();
        } catch (err: any) {
            toast.error(err.response?.data?.detail || "Failed to save component");
        } finally {
            setSubmitting(false);
        }
    };

    const handleSaveStructure = async () => {
        if (!structData.name) {
            toast.error("Structure name is required");
            return;
        }
        try {
            setSubmitting(true);
            if (structData.id) {
                await API.put(`/salary/structures/${structData.id}`, structData);
                toast.success("Structure updated successfully");
            } else {
                await API.post('/salary/structures', structData);
                toast.success("Structure created successfully");
            }
            setIsStructModalOpen(false);
            setStructData({ name: '', description: '', id: '' });
            onRefresh();
        } catch (err: any) {
            toast.error(err.response?.data?.detail || "Failed to save structure");
        } finally {
            setSubmitting(false);
        }
    };

    const handleCreateComponent = handleSaveComponent;
    const handleCreateStructure = handleSaveStructure;

    return (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
            {/* Components Card (unchanged) */}
            <Card className="bg-card border border-white/10 shadow-md rounded-2xl overflow-hidden">
                <CardHeader className="border-b bg-muted/20 pb-4">
                    <div className="flex justify-between items-center">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-primary/10 rounded-lg">
                                <Settings className="h-5 w-5 text-primary" />
                            </div>
                            <div>
                                <CardTitle className="text-lg">Salary Components</CardTitle>
                                <CardDescription className="text-xs">Base earnings & deductions</CardDescription>
                            </div>
                        </div>
                        <Button
                            size="sm"
                            variant="outline"
                            className="rounded-full hover:bg-primary hover:text-white transition-colors"
                            onClick={() => setIsCompModalOpen(true)}
                        >
                            <Plus className="h-4 w-4" />
                        </Button>
                    </div>
                </CardHeader>
                <CardContent className="p-0">
                    <Table>
                        <TableHeader>
                            <TableRow className="hover:bg-transparent border-none">
                                <TableHead className="pl-6 h-12">Name</TableHead>
                                <TableHead className="h-12">Type</TableHead>
                                <TableHead className="text-right h-12">Default Value</TableHead>
                                <TableHead className="w-[80px] h-12 pr-6"></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading && !components.length ? (
                                <TableRow><TableCell colSpan={4} className="text-center py-8"><Loader2 className="animate-spin mx-auto h-6 w-6" /></TableCell></TableRow>
                            ) : components.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={4} className="text-center py-12 text-muted-foreground group">
                                        <div className="flex flex-col items-center gap-2 animate-in fade-in slide-in-from-bottom-2 duration-500">
                                            <Settings className="h-8 w-8 opacity-20 group-hover:rotate-45 transition-transform" />
                                            <p className="text-sm font-bold opacity-60">No Components Defined</p>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ) : components.map((c: any) => (
                                <TableRow key={c.id} className="border-b border-border/50 last:border-0 group">
                                    <TableCell className="pl-6 py-4">
                                        <div className="font-bold text-slate-900 dark:text-gray-100">{c.name}</div>
                                        <div className="text-[10px] text-muted-foreground uppercase">{c.calculation_type}</div>
                                    </TableCell>
                                    <TableCell>
                                        <Badge
                                            variant={c.type === 'Earning' ? 'outline' : 'destructive'}
                                            className={cn(
                                                "text-[10px] font-bold px-2 rounded-full",
                                                c.type === 'Earning' ? "border-emerald-500/50 text-emerald-600 bg-emerald-50/50 dark:bg-emerald-500/10 dark:text-emerald-400" : "bg-red-50/50 text-red-600 border-red-500/50 dark:bg-red-500/10 dark:text-red-400 dark:border-red-500/30"
                                            )}
                                        >
                                            {c.type}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="text-right font-mono font-bold text-sm text-foreground">
                                        {c.calculation_type === 'Percentage' ? `${c.default_value}%` : `₹${c.default_value.toLocaleString()}`}
                                    </TableCell>
                                    <TableCell className="pr-6">
                                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full" onClick={() => handleEditComponent(c)}>
                                                        <Edit2 className="h-3.5 w-3.5" />
                                                    </Button>
                                                </TooltipTrigger>
                                                <TooltipContent side="top">Edit component</TooltipContent>
                                            </Tooltip>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full text-destructive" onClick={() => handleDeleteComponent(c.id)}>
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                    </Button>
                                                </TooltipTrigger>
                                                <TooltipContent side="top">Delete component</TooltipContent>
                                            </Tooltip>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            {/* Structures Card */}
            <Card className="bg-card border-border shadow-md rounded-2xl overflow-hidden border-t-4 border-t-primary/30">
                <CardHeader className="border-b bg-muted/20 pb-4">
                    <div className="flex justify-between items-center">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-primary/10 rounded-lg">
                                <Briefcase className="h-5 w-5 text-primary" />
                            </div>
                            <div>
                                <CardTitle className="text-lg">Salary Structures</CardTitle>
                                <CardDescription className="text-xs">Pay scale templates</CardDescription>
                            </div>
                        </div>
                        <Button
                            size="sm"
                            variant="outline"
                            className="rounded-full hover:bg-primary hover:text-white transition-colors"
                            onClick={() => setIsStructModalOpen(true)}
                        >
                            <Plus className="h-4 w-4" />
                        </Button>
                    </div>
                </CardHeader>
                <CardContent className="p-0">
                    <Table>
                        <TableHeader>
                            <TableRow className="hover:bg-transparent border-none">
                                <TableHead className="pl-6 h-12">Structure Name</TableHead>
                                <TableHead className="h-12">Action</TableHead>
                                <TableHead className="text-right pr-6 h-12"></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading && !structures.length ? (
                                <TableRow><TableCell colSpan={3} className="text-center py-8"><Loader2 className="animate-spin mx-auto h-6 w-6" /></TableCell></TableRow>
                            ) : structures.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={3} className="text-center py-12 text-muted-foreground group">
                                        <div className="flex flex-col items-center gap-2 animate-in fade-in slide-in-from-bottom-2 duration-500">
                                            <Layout className="h-8 w-8 opacity-20 group-hover:scale-110 transition-transform" />
                                            <p className="text-sm font-bold opacity-60">No Salary Structures</p>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ) : structures.map((s: any) => (
                                <TableRow key={s.id} className="border-b border-border/50 last:border-0 hover:bg-muted/30 transition-colors group">
                                    <TableCell className="pl-6 py-4">
                                        <div className="font-bold text-slate-900 dark:text-gray-100">{s.name}</div>
                                        {s.description && <div className="text-[10px] text-muted-foreground line-clamp-1">{s.description}</div>}
                                    </TableCell>
                                    <TableCell>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="h-8 rounded-lg text-[10px] font-bold gap-1.5 border-primary/20 hover:bg-primary/5"
                                            onClick={() => {
                                                setSelectedStructure(s);
                                                setIsManageCompOpen(true);
                                            }}
                                        >
                                            <Settings className="h-3 w-3" /> Manage Components
                                        </Button>
                                    </TableCell>
                                    <TableCell className="text-right pr-6">
                                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity justify-end">
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full" onClick={() => handleEditStructure(s)}>
                                                        <Edit2 className="h-3.5 w-3.5" />
                                                    </Button>
                                                </TooltipTrigger>
                                                <TooltipContent side="top">Edit structure</TooltipContent>
                                            </Tooltip>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full text-destructive" onClick={() => handleDeleteStructure(s.id)}>
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                    </Button>
                                                </TooltipTrigger>
                                                <TooltipContent side="top">Delete structure</TooltipContent>
                                            </Tooltip>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            {/* Component Modal */}
            <Dialog open={isCompModalOpen} onOpenChange={setIsCompModalOpen}>
                <DialogContent className="sm:max-w-[600px] w-[95vw] rounded-[2.5rem] p-0 overflow-hidden border border-white/10 shadow-[0_40px_100px_rgba(0,0,0,0.4)] bg-card/95 backdrop-blur-2xl">
                    <DialogHeader className="bg-gradient-to-br from-[#1e293b] via-[#334155] to-slate-900 px-10 py-12 text-white relative overflow-hidden">
                        <div className="absolute -right-6 -bottom-6 opacity-10 rotate-12 pointer-events-none">
                            <Settings size={220} />
                        </div>
                        <div className="relative z-10 space-y-3">
                            <div className="flex items-center gap-2">
                                <div className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                                <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Architecture Engine</span>
                            </div>
                            <DialogTitle className="text-3xl font-bold tracking-tighter">
                                {compData.id ? "Edit Component" : "Define Component"}
                            </DialogTitle>
                            <DialogDescription className="text-white/60 font-medium">
                                {compData.id ? "Modify this financial building block." : "Forge the essential mathematical building blocks of your payroll system."}
                            </DialogDescription>
                        </div>
                    </DialogHeader>
                    <div className="p-10 space-y-8 bg-background">
                        <div className="space-y-3">
                            <label className="text-[11px] font-bold uppercase tracking-[0.1em] text-muted-foreground/80">Component Identity</label>
                            <Input
                                value={compData.name}
                                onChange={(e) => setCompData({ ...compData, name: e.target.value })}
                                placeholder="e.g. Basic Salary, Medical, TDS"
                                className="rounded-2xl h-16 border-white/5 bg-background focus:ring-primary/20 transition-all text-lg font-bold shadow-inner"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-6">
                            <div className="space-y-3">
                                <label className="text-[11px] font-bold uppercase tracking-[0.1em] text-muted-foreground/80">Value Nature</label>
                                <Select value={compData.type} onValueChange={(v) => setCompData({ ...compData, type: v })}>
                                    <SelectTrigger className="rounded-2xl h-16 border-muted-foreground/20 bg-muted/40 font-bold text-sm shadow-inner">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="rounded-2xl border-none shadow-2xl p-2 backdrop-blur-xl">
                                        <SelectItem value="Earning" className="h-12 rounded-xl mb-1 font-bold text-emerald-600">Earning</SelectItem>
                                        <SelectItem value="Deduction" className="h-12 rounded-xl mb-1 font-bold text-red-600">Deduction</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-3">
                                <label className="text-[11px] font-bold uppercase tracking-[0.1em] text-muted-foreground/80">Calculation Logic</label>
                                <Select value={compData.calculation_type} onValueChange={(v) => setCompData({ ...compData, calculation_type: v })}>
                                    <SelectTrigger className="rounded-2xl h-16 border-muted-foreground/20 bg-muted/40 font-bold text-sm shadow-inner">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="rounded-2xl border-none shadow-2xl p-2 backdrop-blur-xl">
                                        <SelectItem value="Fixed" className="h-12 rounded-xl mb-1 font-bold">Fixed Amount ₹</SelectItem>
                                        <SelectItem value="Percentage" className="h-12 rounded-xl mb-1 font-bold">Percentage %</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="space-y-3">
                            <label className="text-[11px] font-bold uppercase tracking-[0.1em] text-muted-foreground/80">Base Multiplier / Amount</label>
                            <div className="relative">
                                <span className="absolute right-6 top-1/2 -translate-y-1/2 text-primary font-bold opacity-40 text-xl italic">
                                    {compData.calculation_type === 'Percentage' ? '%' : '₹'}
                                </span>
                                <Input
                                    type="number"
                                    value={compData.default_value}
                                    onChange={(e) => setCompData({ ...compData, default_value: parseFloat(e.target.value) })}
                                    className="rounded-2xl h-16 border-muted-foreground/20 bg-muted/40 focus:ring-primary/20 transition-all font-bold text-2xl shadow-inner pr-12"
                                />
                            </div>
                        </div>
                    </div>
                    <div className="px-10 pb-10 flex gap-4">
                        <Button variant="ghost" onClick={() => setIsCompModalOpen(false)} className="rounded-2xl h-16 font-bold text-muted-foreground hover:bg-muted">Discard</Button>
                        <Button onClick={handleCreateComponent} disabled={submitting} className="rounded-2xl flex-1 h-16 text-lg font-bold bg-primary hover:bg-primary/90 text-white shadow-2xl shadow-primary/40 group active:scale-[0.98] transition-all">
                            {submitting ? <Loader2 className="h-6 w-6 animate-spin" /> : (
                                <>
                                    {compData.id ? "Update Component" : "Forge Component"}
                                    <Plus className="ml-3 h-5 w-5 group-hover:rotate-90 transition-transform" />
                                </>
                            )}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Structure Modal */}
            <Dialog open={isStructModalOpen} onOpenChange={setIsStructModalOpen}>
                <DialogContent className="sm:max-w-[600px] w-[95vw] rounded-[2.5rem] p-0 overflow-hidden border border-white/10 shadow-[0_32px_80px_rgba(0,0,0,0.3)] bg-card/95 backdrop-blur-2xl">
                    <DialogHeader className="bg-gradient-to-br from-indigo-900 via-indigo-700 to-indigo-500 px-10 py-12 text-white relative overflow-hidden">
                        <div className="absolute -left-10 -top-10 opacity-10 -rotate-12 pointer-events-none">
                            <Layers size={220} />
                        </div>
                        <div className="relative z-10 space-y-3">
                            <div className="flex items-center gap-2">
                                <div className="h-2 w-2 rounded-full bg-indigo-400 animate-pulse" />
                                <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-indigo-200">System Architect</span>
                            </div>
                            <DialogTitle className="text-3xl font-bold tracking-tighter">
                                {structData.id ? "Edit Pay Structure" : "New Pay Structure"}
                            </DialogTitle>
                            <DialogDescription className="text-indigo-100 font-medium">
                                {structData.id ? "Modify this pay architecture." : "Design a unified pay architecture for your departments."}
                            </DialogDescription>
                        </div>
                    </DialogHeader>
                    <div className="p-10 space-y-8 bg-background">
                        <div className="space-y-3">
                            <label className="text-[11px] font-bold uppercase tracking-[0.1em] text-muted-foreground/80">Architecture Name</label>
                            <Input
                                value={structData.name}
                                onChange={(e) => setStructData({ ...structData, name: e.target.value })}
                                placeholder="e.g. Standard Ops, Senior Management"
                                className="rounded-2xl h-16 border-white/5 bg-background focus:ring-indigo-500/20 transition-all text-lg font-bold shadow-inner"
                            />
                        </div>
                        <div className="space-y-3">
                            <label className="text-[11px] font-bold uppercase tracking-[0.1em] text-muted-foreground/80">Internal Description</label>
                            <textarea
                                value={structData.description}
                                onChange={(e) => setStructData({ ...structData, description: e.target.value })}
                                placeholder="State the purpose of this structure..."
                                className="w-full min-h-[140px] p-6 rounded-2xl border-muted-foreground/20 bg-muted/40 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 transition-all font-bold text-sm resize-none shadow-inner"
                            />
                        </div>
                    </div>
                    <div className="px-10 pb-10 flex gap-4">
                        <Button variant="ghost" onClick={() => setIsStructModalOpen(false)} className="rounded-2xl h-16 font-bold text-muted-foreground hover:bg-muted">Discard</Button>
                        <Button onClick={handleCreateStructure} disabled={submitting} className="rounded-2xl flex-1 h-16 text-lg font-bold bg-indigo-600 hover:bg-indigo-700 text-white shadow-2xl shadow-indigo-600/40 group active:scale-[0.98] transition-all">
                            {submitting ? <Loader2 className="h-6 w-6 animate-spin" /> : (
                                <>
                                    {structData.id ? "Update Structure" : "Initialize Structure"}
                                    <CheckCircle2 className="ml-3 h-5 w-5 group-hover:scale-110 transition-transform" />
                                </>
                            )}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            <ManageComponentsDialog
                isOpen={isManageCompOpen}
                setIsOpen={setIsManageCompOpen}
                structure={selectedStructure}
                allComponents={components}
                onRefresh={onRefresh}
            />
        </div>
    );
};

const ManageComponentsDialog = ({ isOpen, setIsOpen, structure, allComponents, onRefresh }: any) => {
    const [mapping, setMapping] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [newComp, setNewComp] = useState({ component_id: '', value: 0 });

    const fetchMapping = useCallback(async () => {
        if (!structure) return;
        try {
            setLoading(true);
            const res = await API.get(`/salary/structures/${structure.id}/components`);
            setMapping(res.data || []);
        } catch (err) {
            toast.error("Failed to load components for this structure");
        } finally {
            setLoading(false);
        }
    }, [structure]);

    useEffect(() => {
        if (isOpen && structure) fetchMapping();
    }, [isOpen, structure, fetchMapping]);

    const handleAdd = async () => {
        if (!newComp.component_id) return;
        try {
            setSubmitting(true);
            await API.post(`/salary/structures/${structure.id}/components`, newComp);
            toast.success("Component linked successfully");
            setNewComp({ component_id: '', value: 0 });
            fetchMapping();
            onRefresh();
        } catch (err) {
            toast.error("Failed to add component");
        } finally {
            setSubmitting(false);
        }
    };

    const handleRemove = async (mapId: string) => {
        try {
            await API.delete(`/salary/structures/${structure.id}/components/${mapId}`);
            toast.success("Component removed");
            fetchMapping();
            onRefresh();
        } catch (err) {
            toast.error("Failed to remove component");
        }
    };

    const totals = useMemo(() => {
        return mapping.reduce((acc, curr) => {
            if (curr.component_type === 'Earning') acc.earnings += curr.value;
            else acc.deductions += curr.value;
            return acc;
        }, { earnings: 0, deductions: 0 });
    }, [mapping]);

    return (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogContent className="sm:max-w-[600px] w-[95vw] rounded-[2.5rem] p-0 overflow-hidden border border-white/10 shadow-[0_50px_120px_rgba(0,0,0,0.5)] bg-card/95 backdrop-blur-2xl">
                <DialogHeader className="bg-gradient-to-r from-slate-900 via-slate-800 to-primary px-8 py-10 text-white relative">
                    <div className="absolute right-8 top-1/2 -translate-y-1/2 opacity-10 pointer-events-none">
                        <Settings size={200} />
                    </div>
                    <div className="relative z-10 space-y-3">
                        <div className="flex items-center gap-3">
                            <div className="h-2.5 w-2.5 rounded-full bg-primary animate-pulse" />
                            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary-foreground/60 leading-none">Blueprint Modifier</span>
                        </div>
                        <DialogTitle className="text-2xl font-bold tracking-tighter leading-none">
                            {structure?.name} <span className="text-primary/60">Components</span>
                        </DialogTitle>
                        <DialogDescription className="text-white/60 font-medium max-w-[400px] text-xs">
                            Compose and refine the financial DNA for this specific pay architecture.
                        </DialogDescription>
                    </div>
                </DialogHeader>

                <div className="p-8 space-y-8 bg-background/90 max-h-[50vh] overflow-y-auto scrollbar-hide">
                    {/* Summary Bar */}
                    <div className="grid grid-cols-3 gap-4">
                        <div className="bg-card border border-white/10 p-4 rounded-[1.5rem] shadow-lg">
                            <p className="text-[9px] font-bold uppercase text-muted-foreground mb-1 tracking-widest">Earnings</p>
                            <p className="text-xl font-bold text-emerald-500 italic">₹{totals.earnings.toLocaleString()}</p>
                        </div>
                        <div className="bg-card border border-white/10 p-4 rounded-[1.5rem] shadow-lg">
                            <p className="text-[9px] font-bold uppercase text-muted-foreground mb-1 tracking-widest">Deductions</p>
                            <p className="text-xl font-bold text-red-500 italic">₹{totals.deductions.toLocaleString()}</p>
                        </div>
                        <div className="glass-card p-4 rounded-[1.5rem] bg-primary text-white border-primary/20">
                            <p className="text-[9px] font-bold uppercase text-white/60 mb-1 tracking-widest">Net Base</p>
                            <p className="text-xl font-bold italic">₹{(totals.earnings - totals.deductions).toLocaleString()}</p>
                        </div>
                    </div>

                    {/* Add Component Logic */}
                    <div className="space-y-4">
                        <h4 className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                            <Plus className="h-3.5 w-3.5" /> Quick Add
                        </h4>
                        <div className="grid grid-cols-4 gap-3">
                            <div className="col-span-2">
                                <Select value={newComp.component_id} onValueChange={(v) => setNewComp({ ...newComp, component_id: v })}>
                                    <SelectTrigger className="h-14 rounded-2xl border-muted-foreground/20 bg-muted/30 font-bold text-sm">
                                        <SelectValue placeholder="Select component" />
                                    </SelectTrigger>
                                    <SelectContent className="rounded-2xl border-none shadow-2xl p-2 backdrop-blur-xl">
                                        {allComponents.map((c: any) => (
                                            <SelectItem key={c.id} value={c.id} className="h-12 rounded-xl mb-1 font-bold text-xs">{c.name} ({c.type})</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <Input
                                type="number"
                                placeholder="Value"
                                value={newComp.value}
                                onChange={(e) => setNewComp({ ...newComp, value: parseFloat(e.target.value) })}
                                className="h-14 rounded-2xl border-muted-foreground/20 bg-muted/30 font-bold text-lg"
                            />
                            <Button
                                onClick={handleAdd}
                                disabled={submitting || !newComp.component_id}
                                className="h-14 rounded-2xl bg-primary hover:bg-primary/90 text-white font-bold active:scale-[0.98] transition-all"
                            >
                                {submitting ? <Loader2 className="animate-spin h-5 w-5" /> : "Link"}
                            </Button>
                        </div>
                    </div>

                    {/* Component Table */}
                    <div className="space-y-4">
                        <h4 className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                            <Briefcase className="h-3.5 w-3.5" /> Linked Attributes
                        </h4>
                        <div className="rounded-[2rem] border border-white/10 overflow-hidden bg-background/50 backdrop-blur-sm shadow-inner">
                            <Table>
                                <TableHeader className="bg-muted/30 h-14 border-b border-muted-foreground/10">
                                    <TableRow className="hover:bg-transparent">
                                        <TableHead className="pl-6 font-bold text-[9px] uppercase tracking-widest">Financial Head</TableHead>
                                        <TableHead className="text-center font-bold text-[9px] uppercase tracking-widest">Type</TableHead>
                                        <TableHead className="text-right font-bold text-[9px] uppercase tracking-widest pr-6">Value</TableHead>
                                        <TableHead className="w-[80px]"></TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {loading ? (
                                        <TableRow><TableCell colSpan={4} className="h-32 text-center"><Loader2 className="animate-spin mx-auto h-8 w-8 text-primary/40" /></TableCell></TableRow>
                                    ) : mapping.length === 0 ? (
                                        <TableRow><TableCell colSpan={4} className="h-32 text-center text-muted-foreground/60 text-xs font-bold uppercase tracking-tighter">No blueprints linked</TableCell></TableRow>
                                    ) : mapping.map((m) => (
                                        <TableRow key={m.id} className="h-16 border-b border-muted-foreground/5 last:border-0 hover:bg-muted/20 transition-colors group">
                                            <TableCell className="pl-6 font-bold text-sm text-foreground">{m.name}</TableCell>
                                            <TableCell className="text-center">
                                                <Badge
                                                    variant="outline"
                                                    className={cn(
                                                        "text-[9px] font-bold uppercase px-2 py-0.5 rounded-full",
                                                        m.component_type === 'Earning' ? "border-emerald-500/30 text-emerald-600 bg-emerald-50/50 dark:bg-emerald-500/10 dark:text-emerald-400" : "border-red-500/30 text-red-600 bg-red-50/50 dark:bg-red-500/10 dark:text-red-400"
                                                    )}
                                                >
                                                    {m.component_type}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="text-right font-bold text-slate-800 dark:text-gray-200 pr-6">₹{m.value.toLocaleString()}</TableCell>
                                            <TableCell>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => handleRemove(m.id)}
                                                    className="opacity-0 group-hover:opacity-100 h-9 w-9 text-red-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/20 transition-all rounded-xl"
                                                >
                                                    <X className="h-4 w-4" />
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    </div>
                </div>

                <div className="p-8 bg-muted/10 border-t border-muted-foreground/5 flex justify-end">
                    <Button
                        onClick={() => setIsOpen(false)}
                        className="h-14 px-10 rounded-[1.2rem] bg-primary hover:bg-primary/90 text-white font-bold shadow-xl shadow-primary/40 active:scale-95 transition-all text-sm"
                    >
                        Close Blueprint
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

const PayrollDetailView = ({ runId, onBack, setIsFinalizing }: any) => {
    const [run, setRun] = useState<any>(null);
    const [details, setDetails] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    const [isSettlementDialogOpen, setIsSettlementDialogOpen] = useState(false);

    useEffect(() => {
        if (setIsFinalizing) {
            setIsFinalizing(isSettlementDialogOpen);
        }
    }, [isSettlementDialogOpen, setIsFinalizing]);

    const calculatedNet = useMemo(() => {
        return details.reduce((acc, curr) => acc + (curr.net_salary || 0), 0);
    }, [details]);

    const calculatedGross = useMemo(() => {
        return details.reduce((acc, curr) => acc + (curr.gross_salary || 0), 0);
    }, [details]);

    const fetchDetails = useCallback(async () => {
        try {
            setLoading(true);
            const [runRes, detailRes] = await Promise.all([
                API.get(`/salary/payroll-run/${runId}`),
                API.get(`/salary/payroll-run/${runId}/employees`)
            ]);
            setRun(runRes.data);
            setDetails(detailRes.data || []);
        } catch (err) {
            toast.error("Failed to load payroll details");
        } finally {
            setLoading(false);
        }
    }, [runId]);

    useEffect(() => {
        fetchDetails();
    }, [fetchDetails]);

    const handleProcess = async () => {
        try {
            setProcessing(true);
            await API.post(`/salary/payroll-run/${runId}/process`);
            toast.success("Payroll processed and finalized!");
            setIsSettlementDialogOpen(false);
            onBack();
        } catch (err: any) {
            toast.error(err.response?.data?.detail || "Processing failed");
        } finally {
            setProcessing(false);
        }
    };

    if (loading) return (
        <div className="h-screen flex items-center justify-center">
            <Loader2 className="h-10 w-10 animate-spin text-primary" />
        </div>
    );

    if (!run) return (
        <div className="h-screen flex flex-col items-center justify-center space-y-4">
            <p className="text-muted-foreground font-medium text-lg">Payroll run not found.</p>
            <Button onClick={onBack} variant="outline" className="rounded-full">
                Go Back
            </Button>
        </div>
    );

    return (
        <div className="bg-background space-y-6">
            <div className="max-w-[1600px] mx-auto space-y-6">
                <div className="flex items-center gap-4">
                    <Button variant="outline" size="icon" onClick={onBack} className="rounded-full hover:bg-primary/10 hover:border-primary/30 transition-all shadow-md group">
                        <ArrowRight className="h-4 w-4 rotate-180 group-hover:-translate-x-0.5 transition-transform" />
                    </Button>
                    <div className="flex items-center gap-3">
                        <Badge variant="outline" className="bg-primary/5 text-primary border-primary/20 rounded-lg px-3 py-1 text-[10px] font-bold uppercase tracking-widest">
                            {run?.status}
                        </Badge>
                        <span className="text-xs font-bold text-muted-foreground">• {details.length} Records</span>
                    </div>
                    {run?.status !== 'Finalized' && (
                        <Button
                            onClick={() => setIsSettlementDialogOpen(true)}
                            disabled={processing}
                            className="rounded-full shadow-lg px-8 bg-primary h-11 text-sm font-bold hover:scale-105 transition-transform ml-auto"
                        >
                            <CheckCircle2 className="h-4 w-4 mr-2" />
                            Finalize Cycle
                        </Button>
                    )}
                </div>

                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <Card className="bg-card border border-white/10 shadow-lg rounded-2xl transition-all hover:translate-y-[-2px]">
                        <CardContent className="p-6">
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-primary/10 rounded-xl text-primary"><Users className="h-6 w-6" /></div>
                                <div>
                                    <p className="text-sm text-muted-foreground font-medium uppercase tracking-wider">Total Staff</p>
                                    <h3 className="text-2xl font-bold">{details.length}</h3>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="bg-card border border-white/10 shadow-lg rounded-2xl transition-all hover:translate-y-[-2px]">
                        <CardContent className="p-6">
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-emerald-500/10 rounded-xl text-emerald-600"><TrendingDown className="h-6 w-6" /></div>
                                <div>
                                    <p className="text-sm text-muted-foreground font-medium uppercase tracking-wider">Total Gross</p>
                                    <h3 className="text-2xl font-bold text-emerald-700">₹{(run?.status === 'Finalized' ? run?.total_gross : calculatedGross)?.toLocaleString()}</h3>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="bg-card border border-white/10 shadow-lg rounded-2xl transition-all hover:translate-y-[-2px]">
                        <CardContent className="p-6">
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-indigo-500/10 rounded-xl text-indigo-600"><IndianRupee className="h-6 w-6" /></div>
                                <div>
                                    <p className="text-sm text-muted-foreground font-medium uppercase tracking-wider">Net Payable</p>
                                    <h3 className="text-2xl font-bold text-indigo-700">₹{(run?.status === 'Finalized' ? run?.total_net : calculatedNet)?.toLocaleString()}</h3>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Detailed Table */}
                <Card className="bg-card border border-white/10 shadow-md rounded-2xl overflow-hidden">
                    <Table>
                        <TableHeader className="bg-muted/50">
                            <TableRow>
                                <TableHead className="pl-6 h-14">Employee Name</TableHead>
                                <TableHead className="h-14">Structure</TableHead>
                                <TableHead className="text-center h-14">Days</TableHead>
                                <TableHead className="text-right h-14">Gross</TableHead>
                                <TableHead className="text-right h-14">Deductions</TableHead>
                                <TableHead className="text-right h-14 pr-6 font-bold">Net Salary</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {details.map((d: any) => (
                                <TableRow key={d.id} className="hover:bg-muted/30 transition-colors group">
                                    <TableCell className="pl-6 py-5">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center font-bold text-muted-foreground">
                                                {d.full_name?.charAt(0)}
                                            </div>
                                            <div>
                                                <div className="font-bold text-foreground leading-tight">{d.full_name}</div>
                                                <div className="text-[10px] text-muted-foreground uppercase tracking-widest">{d.profile_id?.substring(0, 8)}</div>
                                            </div>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <Badge variant="outline" className="rounded-lg text-[10px] font-bold border-muted-foreground/30">
                                            {d.structure_name}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="text-center">
                                        <div className="flex flex-col items-center">
                                            <span className="font-bold">{d.payable_days}</span>
                                            {d.lop_days > 0 && <span className="text-[9px] text-red-500 font-bold">-{d.lop_days} LOP</span>}
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-right font-medium">₹{d.gross_salary?.toLocaleString()}</TableCell>
                                    <TableCell className="text-right text-red-600/70">₹{d.total_deductions?.toLocaleString()}</TableCell>
                                    <TableCell className="text-right pr-6 font-bold text-foreground">
                                        <div className="text-lg">₹{d.net_salary?.toLocaleString()}</div>
                                        {run?.status === 'Finalized' && (
                                            <Button variant="link" size="sm" className="h-auto p-0 text-[10px] text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                                                <Printer className="h-3 w-3 mr-1" /> Print Slip
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Card>
            </div>

            {/* Professional Settlement Dialog */}
            <Dialog open={isSettlementDialogOpen} onOpenChange={setIsSettlementDialogOpen}>
                <DialogContent className="sm:max-w-[540px] rounded-[2.5rem] p-0 overflow-hidden border border-white/10 shadow-[0_50px_100px_rgba(0,0,0,0.5)] bg-card/95 backdrop-blur-2xl">
                    <DialogHeader className="bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-primary px-10 py-6 text-white relative overflow-hidden">
                        <div className="absolute -right-10 -top-10 opacity-10 rotate-12 pointer-events-none">
                            <ShieldCheck size={280} />
                        </div>
                        <div className="relative z-10 space-y-2">
                            <Badge variant="outline" className="bg-white/10 text-white border-white/20 px-4 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest backdrop-blur-md">
                                Secure Settlement Authorization
                            </Badge>
                            <DialogTitle className="text-3xl font-bold tracking-tighter">Confirm Disbursement</DialogTitle>
                            <DialogDescription className="text-white/70 font-medium text-base max-w-[400px]">
                                You are about to authorize the settlement for the <span className="text-white font-bold underline decoration-primary underline-offset-4">{new Date(run?.year, run?.month - 1).toLocaleString('default', { month: 'long' })}</span> cycle.
                            </DialogDescription>
                        </div>
                    </DialogHeader>

                    <div className="p-4 space-y-2 bg-background">
                        {/* Summary Metrics */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="bg-background/50 border border-white/5 p-3 rounded-[2rem] shadow-inner backdrop-blur-sm">
                                <p className="text-[10px] font-bold uppercase text-muted-foreground tracking-widest mb-1 flex items-center gap-2">
                                    <Users className="h-3 w-3" /> Total Payees
                                </p>
                                <p className="text-2xl font-bold text-foreground">{details.length}</p>
                            </div>
                            <div className="bg-background/50 border border-white/5 p-3 rounded-[2rem] shadow-inner backdrop-blur-sm">
                                <p className="text-[10px] font-bold uppercase text-primary/60 tracking-widest mb-1 flex items-center gap-2">
                                    <IndianRupee className="h-3 w-3" /> Net Payable
                                </p>
                                <p className="text-2xl font-bold text-primary">₹{(run?.total_net || calculatedNet)?.toLocaleString()}</p>
                            </div>
                        </div>

                        {/* Bank Settlement Info */}
                        <div className="p-4 rounded-[1.5rem] bg-background/50 border border-white/5 shadow-inner backdrop-blur-sm relative overflow-hidden">
                            <div className="absolute right-0 top-0 h-full w-24 bg-gradient-to-l from-primary/5 to-transparent pointer-events-none" />
                            <h4 className="text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-2 mb-4">
                                <Landmark className="h-4 w-4 text-primary" /> Settlement Node
                            </h4>
                            <div className="space-y-3">
                                <div className="flex justify-between items-center pb-3 border-b border-border/50">
                                    <span className="text-sm font-medium text-muted-foreground">Source Account</span>
                                    <span className="font-bold text-foreground">AXIS Corp Settlement Hub ****4920</span>
                                </div>
                                <div className="flex justify-between items-center pb-4 border-b border-border/50">
                                    <span className="text-sm font-medium text-muted-foreground">Transfer Protocol</span>
                                    <span className="font-bold text-emerald-600">IMPS / NEFT Batch Mode</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-sm font-medium text-muted-foreground">Execution Date</span>
                                    <span className="font-bold text-foreground flex items-center gap-2">
                                        <Calendar className="h-3 w-3 text-primary" /> Immediate (T+0)
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Reminder Badge */}
                        <div className="flex items-center gap-4 p-4 rounded-2xl bg-amber-500/5 border border-amber-500/20 text-amber-600">
                            <Info className="h-5 w-5 shrink-0" />
                            <p className="text-[11px] font-bold leading-relaxed">
                                Once confirmed, funds will be locked for disbursement and payslips will be generated for all {details.length} employees. This action cannot be reversed.
                            </p>
                        </div>
                    </div>

                    <div className="px-8 pb-6 flex gap-4">
                        <Button
                            variant="ghost"
                            onClick={() => setIsSettlementDialogOpen(false)}
                            className="h-14 rounded-2xl font-bold text-muted-foreground hover:bg-muted px-8"
                        >
                            Review Details
                        </Button>
                        <Button
                            onClick={handleProcess}
                            disabled={processing}
                            className="bg-primary hover:bg-primary/90 text-white h-14 rounded-2xl flex-1 font-bold text-lg shadow-2xl shadow-primary/30 active:scale-[0.98] transition-all group"
                        >
                            {processing ? (
                                <Loader2 className="h-6 w-6 animate-spin" />
                            ) : (
                                <>
                                    Confirm & Disburse
                                    <ArrowRight className="ml-3 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                                </>
                            )}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

